import React, { useState, useEffect } from "react";
import axios from "axios";
import ReactGA from "react-ga4";
import { toast } from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  InputLabel,
  InputAdornment,
  FormControl,
  OutlinedInput,
  Typography,
  Stack,
  Box,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useStyle } from "../../context/styleContext/styleContext";

const ChangePasswordModal = ({ isOpen, close }) => {
  const { style } = useStyle();

  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    newPassword1: "",
    newPassword2: "",
    token: localStorage.getItem("token"),
  });

  useEffect(() => {
    if (!isOpen) return;

    ReactGA.send({
      hitType: "pageview",
      page: "ChangePasswordModal",
      title: "ChangePasswordModal",
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      newPassword1: "",
      newPassword2: "",
      token: localStorage.getItem("token"),
    });
    setShowConfirm(false);
    setShowPassword1(false);
    setShowPassword2(false);
    setSubmitting(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClickShowPassword1 = () => setShowPassword1((show) => !show);
  const handleClickShowPassword2 = () => setShowPassword2((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const closeFunction = () => {
    setShowConfirm(false);
    setFormData({
      newPassword1: "",
      newPassword2: "",
      token: localStorage.getItem("token"),
    });
    close();
  };

  const submitChanges = async (e) => {
    e.preventDefault();

    let okToSend = true;
    const { newPassword1, newPassword2, token } = formData;

    if (!token) {
      okToSend = false;
      toast.error("Error: Account credentials could not be located");
    }

    if (!newPassword1) {
      okToSend = false;
      toast.error("Error: You must enter a new password if you want to change it");
    }

    if (!newPassword2) {
      okToSend = false;
      toast.error("Error: Please enter your password again");
    }

    if (newPassword1.length < 6) {
      okToSend = false;
      toast.error("Password must be at least 6 characters long");
    }

    if (newPassword1.length > 1000) {
      okToSend = false;
      toast.error("Password must be 1000 characters or less");
    }

    if (newPassword1 !== newPassword2) {
      okToSend = false;
      toast.error("Passwords do not match");
    }

    if (!okToSend) return;

    try {
      setSubmitting(true);

      const { data } = await axios.patch("/change_password", formData);

      if (data.error) {
        toast.error(data.error);
        return;
      }

      toast.success("Password successfully changed!");
      setShowConfirm(true);
    } catch (error) {
      console.error(error);
      toast.error("Error: Password update failed");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.color2,
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: style.color2,
      },
      "&:hover fieldset": {
        borderColor: style.color3,
      },
      "&.Mui-focused fieldset": {
        borderColor: style.accentGreen,
      },
    },
    "& .MuiInputLabel-root": {
      color: style.color3,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },
    "input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0px 1000px ${style.color2} inset`,
      WebkitTextFillColor: style.color4,
      caretColor: style.color4,
      borderRadius: "8px",
    },
  };

  return (
    <Dialog
      open={isOpen}
      onClose={closeFunction}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: style.color1,
          border: `1px solid ${style.color2}`,
          borderRadius: "16px",
          color: style.color4,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: style.color4,
          pr: 6,
          borderBottom: showConfirm ? "none" : `1px solid rgba(255,255,255,0.08)`,
        }}
      >
        {showConfirm ? "Password Updated" : "Change Your Password"}

        <IconButton
          onClick={closeFunction}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: style.color3,
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers={!showConfirm}
        sx={{
          borderColor: "rgba(255,255,255,0.08)",
          py: 3,
        }}
      >
        {showConfirm ? (
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(88,166,124,0.16)",
                border: "1px solid rgba(88,166,124,0.30)",
              }}
            >
              <CheckCircleRoundedIcon
                sx={{
                  fontSize: 42,
                  color: style.accentGreen,
                }}
              />
            </Box>

            <Typography
              sx={{
                color: style.color4,
                fontSize: "1.35rem",
                fontWeight: 800,
              }}
            >
              Success
            </Typography>

            <Typography
              sx={{
                color: style.color3,
                lineHeight: 1.65,
                maxWidth: 420,
              }}
            >
              Your password has been successfully changed.
            </Typography>
          </Stack>
        ) : (
          <Box component="form" onSubmit={submitChanges}>
            <Stack spacing={2.25}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LockRoundedIcon sx={{ color: style.color3 }} />
                <Typography
                  sx={{
                    color: style.color4,
                    fontWeight: 800,
                    fontSize: "1.02rem",
                  }}
                >
                  Set a New Password
                </Typography>
              </Stack>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.6,
                  fontSize: "0.95rem",
                }}
              >
                Enter your new password below. Use at least 6 characters.
              </Typography>

              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel htmlFor="change-password-1">New Password</InputLabel>
                <OutlinedInput
                  id="change-password-1"
                  type={showPassword1 ? "text" : "password"}
                  value={formData.newPassword1}
                  onChange={(e) =>
                    setFormData({ ...formData, newPassword1: e.target.value })
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword1}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: style.color3 }}
                      >
                        {showPassword1 ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="New Password"
                />
              </FormControl>

              <FormControl fullWidth size="small" sx={fieldSx}>
                <InputLabel htmlFor="change-password-2">Confirm New Password</InputLabel>
                <OutlinedInput
                  id="change-password-2"
                  type={showPassword2 ? "text" : "password"}
                  value={formData.newPassword2}
                  onChange={(e) =>
                    setFormData({ ...formData, newPassword2: e.target.value })
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword2}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: style.color3 }}
                      >
                        {showPassword2 ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm New Password"
                />
              </FormControl>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          justifyContent: showConfirm ? "center" : "space-between",
        }}
      >
        {showConfirm ? (
          <Button
            onClick={closeFunction}
            sx={{
              borderRadius: "5px",
              textTransform: "none",
              fontWeight: 700,
              color: style.color4,
              backgroundColor: style.accentGreen,
              minWidth: 140,
              "&:hover": {
                backgroundColor: style.accentGreen,
                filter: "brightness(1.05)",
              },
            }}
          >
            OK
          </Button>
        ) : (
          <>
            <Button
              onClick={closeFunction}
              disabled={submitting}
              sx={{
                borderRadius: "5px",
                textTransform: "none",
                fontWeight: 700,
                color: style.color4,
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.10)",
                },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={submitChanges}
              disabled={submitting}
              sx={{
                borderRadius: "5px",
                textTransform: "none",
                fontWeight: 700,
                color: style.color4,
                backgroundColor: style.accentGreen,
                minWidth: 150,
                "&:hover": {
                  backgroundColor: style.accentGreen,
                  filter: "brightness(1.05)",
                },
                "&.Mui-disabled": {
                  color: "rgba(255,255,255,0.45)",
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              {submitting ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} sx={{ color: style.color4 }} />
                  <span>Saving...</span>
                </Stack>
              ) : (
                "Submit"
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;